import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import { Counterparty } from "@common/types/counterparty";
import { HistoryField } from "@common/constants/history-source-types";

export interface CounterpartyVersion {
    versionId: string;
    logDate: Date;
    changeType: AuditLogChangeType;
    counterpartyName: string;
    diffs: AuditLogDiff[];
}

export interface CounterpartyFieldDef {
    field: keyof Counterparty | HistoryField.TIN_TYPE;
    label: string;
    /**
     * Maps the generically formatted diff value to what the row should read.
     * Used by fields whose stored value is not what a reader expects to see,
     * e.g. a `disabledAt` timestamp presented as an enabled/disabled status.
     */
    presentValue?: (value: string | null) => string | null;
}

interface CounterpartyFieldSection {
    title: string;
    fields: CounterpartyFieldDef[];
}

// Single source of truth for which counterparty diffs are displayable and how
// they are labelled: the drawer body renders these sections, and the header
// label lookup below is derived from them.
export const COUNTERPARTY_FIELD_SECTIONS: CounterpartyFieldSection[] = [
    {
        title: "Basic info",
        fields: [
            { field: "name", label: "Display name" },
            { field: "companyName", label: "Company" },
            { field: "email", label: "Email" },
            { field: "cc", label: "CC" },
            { field: "bcc", label: "BCC" },
            { field: "phoneNumber", label: "Phone number" },
            { field: "website", label: "Website" },
        ],
    },
    {
        title: "Contact name",
        fields: [
            { field: "title", label: "Title" },
            { field: "firstName", label: "First name" },
            { field: "middleName", label: "Middle name" },
            { field: "lastName", label: "Last name" },
            { field: "suffix", label: "Suffix" },
        ],
    },
    {
        title: "Address",
        fields: [
            { field: "country", label: "Country" },
            { field: "street", label: "Street line 1" },
            { field: "street2", label: "Street line 2" },
            { field: "city", label: "City" },
            { field: "state", label: "State" },
            { field: "postalCode", label: "ZIP" },
        ],
    },
    {
        title: "Other",
        fields: [
            { field: "directionOverride", label: "Classification" },
            { field: HistoryField.TIN_TYPE, label: "Tax ID type" },
            { field: "note", label: "Note" },
            {
                field: "disabledAt",
                label: "Status",
                presentValue: (value) => (value ? "Disabled" : "Enabled"),
            },
        ],
    },
];

const COUNTERPARTY_DISPLAYABLE_FIELDS: Record<string, string> =
    Object.fromEntries(
        COUNTERPARTY_FIELD_SECTIONS.flatMap((section) =>
            section.fields.map(({ field, label }) => [field, label]),
        ),
    );

export const getCounterpartyFieldLabel = (field: string): string | undefined =>
    COUNTERPARTY_DISPLAYABLE_FIELDS[field];

export const filterCounterpartyDisplayableDiffs = (
    diffs: HistoryDetailsItem["diff"],
): AuditLogDiff[] =>
    diffs.filter((diff) => {
        const fieldLabel = getCounterpartyFieldLabel(diff.field);
        if (!fieldLabel) {
            return false;
        }
        const hasLabel = diff.before.valueLabel ?? diff.after.valueLabel;
        const hasValue =
            diff.before.value !== null || diff.after.value !== null;
        return hasLabel ?? hasValue;
    });
